import React, { useEffect, useMemo, useState } from 'react';
import appStoreBadge from '../../assets/badges/app-store-badge.png';
import googlePlayBadge from '../../assets/badges/google-play-badge.png';
import { getDevicePlatform } from '../../utils/devicePlatform';

const APP_STORE_URL = (process.env.REACT_APP_APP_STORE_URL || '').trim();
const PLAY_STORE_URL = (process.env.REACT_APP_PLAY_STORE_URL || '').trim();

/** Skip nodes marked for exclusion when capturing the ticket for print/PDF/photo. */
export const excludeFromTicketCapture = (node: HTMLElement): boolean =>
  node.getAttribute('data-exclude-capture') !== 'true';

type StoreBadge = {
  href: string;
  label: string;
  badge: string;
};

const SuccessAppDownloadBanner: React.FC = () => {
  const [platform, setPlatform] = useState<'ios' | 'android' | 'other'>(
    'other',
  );

  useEffect(() => {
    setPlatform(getDevicePlatform());
  }, []);

  const storeBadges = useMemo((): StoreBadge[] => {
    const badges: StoreBadge[] = [];

    const includeAppStore =
      (platform === 'ios' || platform === 'other') && !!APP_STORE_URL;
    const includePlayStore =
      (platform === 'android' || platform === 'other') && !!PLAY_STORE_URL;

    if (includeAppStore) {
      badges.push({
        href: APP_STORE_URL,
        label: 'Download on the App Store',
        badge: appStoreBadge,
      });
    }

    if (includePlayStore) {
      badges.push({
        href: PLAY_STORE_URL,
        label: 'Get it on Google Play',
        badge: googlePlayBadge,
      });
    }

    return badges;
  }, [platform]);

  if (storeBadges.length === 0) {
    return null;
  }

  return (
    <div data-exclude-capture="true" className="border-t border-gray-200 pt-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="font-medium text-gray-900">
            Get the Modesto On Ice app
          </p>
          <p className="text-sm text-blue-800 mt-1">
            Download the app for easy access to your ticket.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {storeBadges.map(({ href, label, badge }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={label}
              aria-label={label}
              className="inline-flex items-center"
            >
              <img src={badge} alt={label} className="h-11 w-auto" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuccessAppDownloadBanner;
